import {type AxiosRequestConfig} from 'axios';

import {spotheroPatch} from 'api';
import {type ApiResponse} from 'api/api.types';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';

import {
    type UpdateCompPassesParams,
    type UpdateCompPassesRawResponse,
    type UpdateCompPassesResult,
} from 'api/comp-passes/comp-pass.types';

const formatRequestParams = (params: UpdateCompPassesParams) => {
    const formattedRequest = convertKeysToSnakeCase(params);

    return formattedRequest;
};

const formatResponse = (
    response: ApiResponse<UpdateCompPassesRawResponse>
): UpdateCompPassesResult => {
    const formattedData = camelizeKeys<
        UpdateCompPassesRawResponse,
        UpdateCompPassesResult
    >(response.data.data);

    return formattedData;
};

/**
 * Bulk-updates the bill-back price and requested-by fields for comp passes.
 *
 * A successful (2xx) response can still contain per-pass failures in
 * `updateErrors` (partial success) — callers should apply updates only to
 * `updatedCompPassIds` and surface `updateErrors` rather than treating them as
 * a thrown error.
 *
 * @param facilityId - The id of the facility the comp passes belong to
 * @param params - The updates to apply, one entry per comp pass
 * @param config - Additional Axios configuration options for the request.
 * @param config.signal - An abort signal to cancel the request.
 *
 * @see '/api/v1/facilities/{facilityId}/comp-pass/passes/'
 */
export const updateCompPasses = async (
    facilityId: number,
    params: UpdateCompPassesParams,
    config: AxiosRequestConfig = {}
): Promise<UpdateCompPassesResult> => {
    try {
        const formattedParams = formatRequestParams(params);

        const response = await spotheroPatch<UpdateCompPassesRawResponse>(
            `facilities/${facilityId}/comp-pass/passes/`,
            formattedParams,
            config
        );

        const formattedResponse = formatResponse(response);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw error;
    }
};
